import { noop } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { combineLatest, EMPTY, Observable, of, Subject } from "rxjs";
import {
  catchError,
  map,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
} from "rxjs/operators";
import { string } from "yup";

import { useFileLoader } from "../../../../../../../components/hooks/file-loader";
import { VerificationDTO } from "../../../../../../../store/verification/models/dto/due-diligence";
import { DueDiligenceFileCategory } from "../../../../../../../store/verification/models/internal";
import { VerificationService } from "../../../../../../../store/verification/service";
import { ConfigContext } from "../../../common/config-context";
import { AttachmentFormType } from "../common/attachment";

export const useFileUploader = (
  category: typeof DueDiligenceFileCategory.T,
  emailSubject: Observable<string>
) => {
  const {
    state: { dueDiligenceId, code },
  } = useContext(ConfigContext);
  const fileLoader = useFileLoader();

  const adminValidation = VerificationDTO.Requests.DueDiligence.Form.Post.Attachments.useFields();
  const participantValidation = VerificationDTO.Requests.DueDiligence.Form.Post.ParticipantAttachments.useFields();

  const [uploaded, setUploaded] = useState(false);
  const subject = useMemo(() => new Subject<AttachmentFormType>(), []);
  const success = useMemo(
    () =>
      new Subject<VerificationDTO.Requests.DueDiligence.Form.Post.Attachments.T>(),
    []
  );

  const validatedEmail$ = useMemo(
    () =>
      emailSubject.pipe(
        switchMap(async (x) => {
          return string()
            .email()
            .validate(x)
            .then(() => {
              return x;
            });
        }),
        catchError(() => EMPTY),
        shareReplay(1)
      ),
    [emailSubject]
  );

  useEffect(() => {
    // This is the participant's flow - we don't have the user's email
    if (code) {
      const sub = subject
        .pipe(
          map((file) => ({
            ...file,
            category,
          })),
          switchMap(
            (x) =>
              new Observable<VerificationDTO.Requests.DueDiligence.Form.Post.ParticipantAttachments.T>(
                (observer) => {
                  participantValidation
                    .validate(x)
                    .then(() => {
                      observer.next(x);
                      observer.complete();
                    })
                    .catch(() => observer.complete());
                }
              )
          )
        )
        .subscribe((data) => {
          fileLoader(
            VerificationService.postDueDiligenceFormAttachmentsParticipant(
              code,
              data
            )
          )
            .then(() => {
              setUploaded(true);
              success.next(data);
            })
            .catch(noop);
        });

      return () => sub.unsubscribe();
    } else {
      const sub = combineLatest([subject, validatedEmail$])
        .pipe(
          startWith(undefined),
          pairwise(),
          switchMap<
            [
              (
                | [
                    VerificationDTO.Requests.DueDiligence.Form.Post.Attachments.T,
                    string
                  ]
                | undefined
              ),
              (
                | undefined
                | [
                    VerificationDTO.Requests.DueDiligence.Form.Post.Attachments.T,
                    string
                  ]
              )
            ],
            Observable<VerificationDTO.Requests.DueDiligence.Form.Post.Attachments.T>
          >(([prev, curr]) => {
            if (curr === undefined) return EMPTY;

            if (prev === undefined) {
              const [file, emailAddress] = curr;
              return of({
                ...file,
                category,
                emailAddress,
              });
            }

            const [_, prevEmail] = prev;
            const [currFile, currEmail] = curr;

            if (prevEmail === currEmail) {
              return of({
                ...currFile,
                category,
                emailAddress: currEmail,
              });
            } else {
              return of({
                ...currFile,
                category,
                emailAddress: currEmail,
                prevEmailAddress: prevEmail,
              });
            }
          }),
          switchMap(
            (x) =>
              new Observable<VerificationDTO.Requests.DueDiligence.Form.Post.Attachments.T>(
                (observer) => {
                  adminValidation
                    .validate(x)
                    .then(() => {
                      observer.next(x);
                      observer.complete();
                    })
                    .catch(() => observer.complete());
                }
              )
          )
        )
        .subscribe((data) => {
          if (dueDiligenceId) {
            fileLoader(
              VerificationService.postDueDiligenceFormAttachments(
                dueDiligenceId,
                data
              )
            )
              .then(() => {
                setUploaded(true);
                success.next(data);
              })
              .catch(noop);
          }
        });

      return () => sub.unsubscribe();
    }
  }, [
    dueDiligenceId,
    subject,
    category,
    validatedEmail$,
    setUploaded,
    success,
  ]);

  return [subject, uploaded, success] as const;
};
